import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT, type Lang } from '../i18n';
import { api } from '../api/client';

/**
 * User tag in the topbar, expanded into a small profile dropdown:
 * shows the user's name/email and the canonical app-language choice.
 * Language changes are applied immediately and persisted server-side.
 */
export function ProfileMenu() {
  const { user } = useAuth();
  const { t, lang, setLang, languages } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chooseLang = (next: Lang) => {
    setLang(next);                                       // apply immediately (also persisted in localStorage)
    api.patch('/api/profile', { lang: next }).catch(() => {});  // persist server-side; UI already updated
  };

  if (!user) return null;

  return (
    <div className="profile-menu" ref={rootRef}>
      <button
        type="button"
        className="user-tag profile-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('profile.menuAria')}
        title={t('profile.title')}
      >
        <div className="user-initial">{user.initials}</div>
        <span>{user.name}</span>
        <ChevronDown size={12} className={`profile-menu-chevron ${open ? 'profile-menu-chevron--open' : ''}`} />
      </button>

      {open && (
        <div className="profile-menu-panel" role="menu">
          <div className="profile-menu-header">
            <div className="profile-menu-name">{user.name}</div>
            {user.email && <div className="profile-menu-email">{user.email}</div>}
          </div>
          <div className="profile-menu-divider" />
          <div className="profile-menu-section-label">{t('common.language')}</div>
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={lang === l.code}
              className={`profile-menu-item ${lang === l.code ? 'profile-menu-item--active' : ''}`}
              onClick={() => chooseLang(l.code)}
            >
              <span>{l.label}</span>
              {lang === l.code && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
